import { PropAsset } from './PropAsset';

export interface ImagePropConfig {
  id: string;
  width: number;
  height: number;
  walkable: boolean;
  collisionRadius: number;
  occludesPlayer?: boolean;
  imagePaths: string[];       // One path per variant
  fallback?: PropAsset;       // Procedural fallback until loaded
}

export class ImagePropAsset extends PropAsset {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly walkable: boolean;
  readonly collisionRadius: number;
  readonly occludesPlayer: boolean;

  private images: (HTMLImageElement | null)[] = [];
  private loaded: boolean[] = [];
  private fallback?: PropAsset;
  private loadPromise: Promise<void>;

  constructor(config: ImagePropConfig) {
    super();
    this.id = config.id;
    this.width = config.width;
    this.height = config.height;
    this.walkable = config.walkable;
    this.collisionRadius = config.collisionRadius;
    this.occludesPlayer = config.occludesPlayer ?? false;
    this.fallback = config.fallback;

    // Start loading all variant images (non-blocking)
    const loadPromises: Promise<void>[] = [];

    for (let i = 0; i < config.imagePaths.length; i++) {
      this.images.push(null);
      this.loaded.push(false);

      const img = new Image();
      const promise = new Promise<void>((resolve) => {
        img.onload = () => {
          this.images[i] = img;
          this.loaded[i] = true;
          this.clearCache();
          resolve();
        };
        img.onerror = () => {
          // Keep fallback for this variant - image not found
          this.loaded[i] = true; // Mark as "done" so waitForLoad resolves
          resolve();
        };
      });
      img.src = config.imagePaths[i];
      loadPromises.push(promise);
    }

    this.loadPromise = Promise.all(loadPromises).then(() => {});
  }

  waitForLoad(): Promise<void> {
    return this.loadPromise;
  }

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, scale: number, variant?: number): void {
    const variantIdx = variant ?? 0;
    const img = this.images[variantIdx] ?? this.images[0];

    if (img) {
      // Draw the loaded image, anchored at bottom-center
      const w = this.width * scale;
      const h = this.height * scale;
      ctx.drawImage(img, cx - w / 2, cy - h, w, h);
    } else if (this.fallback) {
      // Image not loaded yet, use procedural fallback
      this.fallback.draw(ctx, cx, cy, scale, variant);
    }
  }
}
